import { type CatalogueItem, guest, type RatingSubject } from '@croissants/contracts';
import {
  deliveryCourierOut,
  type GuestOrderViewInput,
  guestOrderMoves,
} from '@croissants/contracts/projections';
import { createEndpointHooks, queryKeyFor } from '@croissants/ui';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, isRetriable } from '../lib/api';
import { clearIdempotencyKey, idempotencyKeyFor } from '../lib/idempotency';
import { windowsItemIdsOf } from '../lib/windows';
import { wireLinesOf } from '../lib/wire-lines';
import type { BasketState } from '../state/basket';

/**
 * Every server read and write this app performs, in one module, driven by the
 * endpoint DESCRIPTOR through the shared layer in `@croissants/ui` - the cache
 * key, the invalidation set and the idempotency key all come from the contract
 * rather than being spelled out per hook.
 *
 * TanStack Query owns server state; component state stays local and is never a
 * copy of a response. Three decisions are worth naming:
 *
 *  - An active order polls, at one of two cadences. Provider events must reach
 *    operations within ten seconds (PRD NFR-004) and a guest watching an order
 *    expects the same; a ten-second poll is the honest way to do that without a
 *    socket the worker does not offer. While a courier is actually out it is
 *    five, because the live map (GST-035) rides on this timer and the poll
 *    should not be the larger half of the lag - see `orderPollIntervalMs`. A
 *    settled order stops polling - nothing will change again.
 *  - Mutations that move money carry an idempotency key minted per attempt and
 *    persisted (PRD PAY-006), so the retry of a dropped request is the same
 *    order, not a second one. That is why they name their own key rather than
 *    taking the layer's fresh-per-attempt default.
 *  - Every read retries on `isRetriable`, not on a bare count (TST-001, and the
 *    same predicate the QR session query uses - see `lib/api`). A definitive
 *    refusal - not found, forbidden, a bad request - answers the same way every
 *    time; retrying it twice bought nothing but a longer window in which the
 *    query was neither settled nor running, which is exactly where a guest was
 *    found on an unattended spinner. Only a failure that could plausibly answer
 *    differently - a dropped connection, a 5xx, a 429 - is retried.
 */

const ACTIVE_ORDER_POLL_MS = 10_000;

/*
 * Five seconds while a rider is moving, and the number is chosen against the
 * PROVIDER's cadence rather than against how fast a map could redraw. Uber
 * Direct reports a courier position every 20 to 30 seconds and Stuart every 30,
 * so a ten-second poll made the platform the larger half of the delay a guest
 * watching the pin actually sees; at five it is the smaller half, and past that
 * the poll is asking about a position nobody has updated.
 *
 * Not lower, for two reasons that are both about what is on the other end. The
 * reader is a phone on a Paris apartment's wifi, so every request it does not
 * need is battery and a chance to stall; and every one of them is a worker
 * request against a budget the whole platform shares. Halving the interval
 * doubles both, and buys nothing below the rate the provider speaks at.
 */
const COURIER_OUT_POLL_MS = 5_000;

/**
 * How often this order's page should re-ask, or `false` for never.
 *
 * The outer gate is `guestOrderMoves` and stays exactly what it was: an order
 * that is over is never polled again, whatever its delivery says. Only inside
 * that answer does the courier decide the cadence.
 *
 * A named function rather than a lambda in the options object, because this is
 * a product rule with three answers and it is worth being able to test all
 * three on node - which a `refetchInterval` closure over a query result is not.
 */
export function orderPollIntervalMs(lifecycle: GuestOrderViewInput): number | false {
  if (!guestOrderMoves(lifecycle)) return false;
  return deliveryCourierOut(lifecycle.delivery) ? COURIER_OUT_POLL_MS : ACTIVE_ORDER_POLL_MS;
}

/**
 * How often the Orders screen should re-ask, or `false` for never: at the
 * moving-order cadence while any active row can still change (GST-034).
 *
 * It never re-asked, and focus does not refetch in this app, so a list read a
 * few seconds after checkout went on badging that order "Today" long after the
 * counter had marked it ready. The list is a summary with no courier pin, so
 * the five-second courier cadence is not borrowed.
 */
export function ordersPollIntervalMs(
  list: { readonly active: readonly { readonly lifecycle: GuestOrderViewInput }[] } | undefined,
): number | false {
  return list?.active.some((order) => guestOrderMoves(order.lifecycle)) === true
    ? ACTIVE_ORDER_POLL_MS
    : false;
}

/** Retries only what might succeed on a second attempt; settles everything else at once. */
const RETRY_IF_RETRIABLE = (failureCount: number, error: unknown): boolean =>
  isRetriable(error) && failureCount < 2;

/**
 * Files the scan's own answers for the first screen (`GuestFirstScreen`) under
 * the keys that screen asks with - each read below with no parameter, which is
 * what a fresh scan asks - so it opens on a cache rather than on a second wave
 * of requests. A screen asking anything else (a stored basket, a chosen
 * morning) misses and asks the server, exactly as before.
 */
export function seedFirstScreen(
  queryClient: QueryClient,
  firstScreen: guest.GuestFirstScreen,
): void {
  const seed = (key: readonly unknown[], data: unknown): void => {
    if (data !== undefined) queryClient.setQueryData(key, data);
  };
  seed(queryKeyFor(guest.getStorefront, { query: {} }), firstScreen.storefront);
  seed(queryKeyFor(guest.getShopFacts, {}), firstScreen.shopFacts);
  seed(queryKeyFor(guest.listWindows, { query: {} }), firstScreen.windows);
  seed(queryKeyFor(guest.listShops, { query: {} }), firstScreen.shops);
}

/** This mount authenticates with a signed session token held in the client module. */
function useGuestClient() {
  return api;
}

const { useEndpointQuery, useEndpointMutation } = createEndpointHooks(useGuestClient);

/**
 * The catalogue for this morning - and, in mode A, for this WINDOW.
 *
 * Mode A has no shop-list step: the picker is followed straight by this menu,
 * and the bakery behind it is allocated by the server as this request is served
 * (GST-029, ALC-003). The chosen window is an input to that allocation - the
 * closest bakery that can genuinely serve the slot, which is not always the
 * closest bakery, because the closest one may be shut or open too late. So it
 * travels with the request and belongs in the key: the same morning at two
 * different half-hours can be two different counters.
 */
export function useStorefront(
  serviceDay: string | null,
  shopId: string | null,
  windowStart: string | null,
  enabled = true,
  /**
   * What the guest already holds, when the shelf should answer FOR it (issue
   * #1420): the server then offers only what one counter can hand over with
   * the basket, so no combination that would be refused at Pay is shown. The
   * previous shelf stays on screen while the new one loads, so adding an item
   * never blanks the menu.
   */
  heldItemIds: readonly string[] = [],
) {
  /* The morning the shelf on screen is for, as the server answered it. */
  const shownDay = useRef<string | null | undefined>(undefined);
  const query = useEndpointQuery(
    guest.getStorefront,
    {
      query: {
        ...(serviceDay === null ? {} : { serviceDay }),
        ...(shopId === null ? {} : { shopId }),
        ...(windowStart === null ? {} : { windowStart }),
        ...(heldItemIds.length === 0 ? {} : { itemIds: [...heldItemIds] }),
      },
    },
    {
      enabled,
      keepPreviousData: shelfStandsIn(shownDay.current, serviceDay, heldItemIds.length > 0),
      retry: RETRY_IF_RETRIABLE,
    },
  );
  if (query.data !== undefined && !query.isPlaceholderData) {
    shownDay.current = query.data.serviceDays[0] ?? null;
  }
  return query;
}

/**
 * May the shelf on screen stay there while the next one loads?
 *
 * Yes while the guest holds something (issue #1420), and yes when the SAME
 * MORNING is being asked again - a window settled, which narrows the counters
 * that count and rarely the products. That second case is the scan in an
 * immediate-only world: the shelf answers with no window, `useImmediateWindow`
 * then settles today's soonest one, and the re-ask used to blank the whole
 * menu to a loading state it had just left (docs/portal-latency-audit.html,
 * eighth pass). A DIFFERENT morning is a different shelf and is never stood in
 * for: products that one does not bake must not be shown as tappable. The
 * morning on screen is the one the SERVER answered for, so a shelf first asked
 * with no morning counts as the default one it resolved to - which is the
 * morning the settled draft then names.
 */
export function shelfStandsIn(
  shownDay: string | null | undefined,
  askedDay: string | null,
  holding: boolean,
): boolean {
  if (holding) return true;
  return shownDay !== undefined && shownDay !== null && shownDay === askedDay;
}

/**
 * Mode B: the brands that can serve this address (GST-030, GST-031).
 *
 * This is the FIRST step in mode B - the brand is chosen before the morning,
 * because a brand has many Paris counters and which of them serves is
 * allocation's answer once the day and window are known, not the guest's. So
 * there is no window to filter this list by and no morning to key it on: the
 * question is only "who can serve this apartment at all", and the server answers
 * for the apartment's own next orderable morning.
 */
export function useShops(enabled = true) {
  return useEndpointQuery(guest.listShops, { query: {} }, { enabled, retry: RETRY_IF_RETRIABLE });
}

/**
 * Mode B2: what the bound shop offers, with nobody named (GST-030, GST-045).
 *
 * The one read the first screen is drawn from - whether the shop is open now,
 * today's delivery line, the week it keeps, how far it is and the minimum
 * basket - and the ONLY place the minimum basket reaches a B2 guest, since
 * `GET /g/storefront` answers `shop: null` in this mode by design.
 *
 * It takes no parameter because the guest chooses nothing: the shop is a
 * signed claim on the session token. `enabled` is therefore the mode test and
 * nothing else - the server refuses a mode A or mode B session here - and every
 * caller passes `boundToOneShop(session.storefrontMode)`.
 *
 * Refetched rather than held for the session: `orderableNow` is a fact about
 * the minute it was computed in, and a guest who scanned at 12:58 must not
 * still be offered "Order now" at 13:05.
 */
export function useShopFacts(enabled = true) {
  return useEndpointQuery(
    guest.getShopFacts,
    {},
    { enabled, staleTime: 30_000, retry: RETRY_IF_RETRIABLE },
  );
}

/**
 * The offerable 30-minute windows, per day, with the cutoff (GST-002, RTE-028).
 *
 * `itemIds` is SENT, because per-product caps are part of capacity (RTE-008):
 * a morning is offered only when the counter can make what is in the basket.
 * It was off until 2026-08-30 - the worker validated the field as an array but
 * read repeated parameters as a single string, so `itemIds=a&itemIds=b` came
 * back 400 and took the whole picker with it. The server parses them now.
 *
 * WHAT IS SENT IS THE BASKET'S PRODUCTS, NOT ITS LINES. A set is not baked;
 * its parts are, so it is named by them - `windowsItemIdsOf`, which needs the
 * shelf to read the card. A screen that has not loaded one (the picker itself)
 * sends what the basket alone can say, and that is deliberate: an empty
 * `items` narrows the question rather than answering it wrongly.
 */
export function useWindows(
  basket: BasketState,
  serviceDay: string | null,
  enabled = true,
  /** This screen's loaded shelf, for reading a set's card. Empty when it has none. */
  items: readonly CatalogueItem[] = [],
) {
  const itemIds = windowsItemIdsOf(basket, items);
  return useEndpointQuery(
    guest.listWindows,
    {
      query: {
        ...(serviceDay === null ? {} : { serviceDay }),
        ...(basket.shopId === null ? {} : { shopId: basket.shopId }),
        ...(itemIds.length === 0 ? {} : { itemIds: [...itemIds] }),
      },
    },
    {
      enabled,
      // The cutoff moves and capacity is consumed by other guests while this
      // screen is open, so it is refetched rather than trusted for the session.
      staleTime: 30_000,
      retry: RETRY_IF_RETRIABLE,
    },
  );
}

/** The itemised total the guest will be charged (GST-022). */
export function useQuote(
  basket: BasketState,
  serviceDay: string | null,
  windowStart: string | null,
  /** RTE-034: today's ASAP row rather than a time the guest named on it. */
  soonest: boolean,
  /**
   * The further mornings of a repeat stay (GST-010). Given these, the quote
   * prices every one of them and returns the stay total - which is what the
   * guest is charged at checkout, and so what the review screen must show.
   */
  repeatServiceDays: readonly string[] = [],
) {
  const enabled = basket.lines.length > 0 && serviceDay !== null && windowStart !== null;
  return useEndpointQuery(
    guest.quote,
    {
      /*
       * The body IS the key for a POST-shaped read (`queryKeyFor`), and it
       * carries exactly what is priced: the two products are priced differently
       * so `soonest` is part of it, and the stay is part of the price so its
       * further mornings are too. A basket line also carries the label the guest
       * saw, and that is deliberately NOT sent - a catalogue that renames an
       * item must not look like a new basket and re-quote one. Its ANSWERS are
       * sent (CAT-018): the server reads every formule line by the checkout's
       * rule, and a formule quoted without them is refused for the question it
       * left open - which is what the checkout page showed on 2026-08-29.
       */
      body: {
        shopId: basket.shopId,
        lines: wireLinesOf(basket.lines),
        serviceDay: serviceDay ?? '',
        windowStart: windowStart ?? '',
        soonest,
        ...(repeatServiceDays.length === 0 ? {} : { repeatServiceDays: [...repeatServiceDays] }),
      },
    },
    { enabled, retry: RETRY_IF_RETRIABLE },
  );
}

/**
 * How long the basket has to stop changing before the line under the address
 * asks what it saves. A guest tapping + five times is asking one question, not
 * five, and every distinct basket is a courier pricing call at the provider.
 */
const DELIVERY_OFFER_SETTLE_MS = 600;

/**
 * GST-044: what reaching the delivery grid's next step saves THIS basket
 * (founder, 2026-09-21) - the sentence the line under the address states once
 * there is anything in the basket.
 *
 * The server's answer, priced on the quote's own courier question
 * (`makeDeliveryOffer`); nothing here derives a figure. Asked once the basket
 * has SETTLED, and the previous answer is kept on screen while the next one
 * loads, so the sentence never blinks out between two taps.
 *
 * `serviceDay` is the draft's morning - a stay's FIRST, because the saving is
 * stated per order and every morning carries the same basket - else the
 * session's next service day; the window is the draft's, or none yet - the
 * server then asks about the bag leaving the counter as soon as it can, as an
 * order for now would.
 */
export function useDeliveryOffer(
  basket: BasketState,
  serviceDay: string | null,
  windowStart: string | null,
  soonest: boolean,
) {
  const body = {
    shopId: basket.shopId,
    lines: wireLinesOf(basket.lines),
    serviceDay: serviceDay ?? '',
    ...(windowStart === null ? {} : { windowStart }),
    soonest,
  };
  const settled = useSettled(body, DELIVERY_OFFER_SETTLE_MS);
  const enabled = settled.lines.length > 0 && settled.serviceDay !== '';
  return useEndpointQuery(
    guest.deliveryOffer,
    { body: settled },
    { enabled, keepPreviousData: true, staleTime: 60_000, retry: RETRY_IF_RETRIABLE },
  );
}

/**
 * The value once it has stopped changing for `ms`. Compared by its JSON, so a
 * re-render that rebuilds an equal object is not a change.
 */
function useSettled<T>(value: T, ms: number): T {
  const key = JSON.stringify(value);
  const [settled, setSettled] = useState<{ key: string; value: T }>({ key, value });
  useEffect(() => {
    if (key === settled.key) return;
    const timer = setTimeout(() => setSettled({ key, value }), ms);
    return () => clearTimeout(timer);
  }, [key, ms, settled.key, value]);
  return settled.value;
}

/**
 * How to reach us (GST-043). Asked with or without a session - the card's
 * small code opens the page cold - and asked fresh each time the page opens,
 * because the point of the page is that its numbers can change under it.
 */
export function useSupport() {
  return useEndpointQuery(guest.getSupport, {}, { retry: RETRY_IF_RETRIABLE, staleTime: 0 });
}

export function useOrders(enabled = true) {
  return useEndpointQuery(
    guest.listOrders,
    { query: { limit: 20 } },
    { enabled, retry: RETRY_IF_RETRIABLE, refetchInterval: ordersPollIntervalMs },
  );
}

export function useOrder(orderPublicId: string | null) {
  return useEndpointQuery(
    guest.getOrder,
    { params: { orderPublicId: orderPublicId ?? '' } },
    {
      enabled: orderPublicId !== null,
      retry: RETRY_IF_RETRIABLE,
      /*
       * Polling asks ONE question, of the table that decides which order screen
       * this is: can this order still change under the reader (GST-008)?
       *
       * It used to ask two, and the second was a literal array of seven "settled"
       * statuses which omitted `UNALLOCATED`, `COURIER_UNAVAILABLE` and
       * `MANUAL_INTERVENTION` - so an order the platform had given up on kept
       * calling the API every few seconds for as long as the tab stayed open. The
       * live courier map rode on the same timer through a second, explicitly
       * different rule, on the grounds that a visible map must always poll; it
       * cannot outlive the answer here, because a map is only visible while a
       * courier is out, and a courier being out is exactly what LIVE means. It
       * does now decide the CADENCE, which is a different thing and stays inside
       * that one answer: `orderPollIntervalMs`.
       */
      refetchInterval: (order) =>
        order === undefined ? false : orderPollIntervalMs(order.lifecycle),
    },
  );
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCheckout(scope: string) {
  return useEndpointMutation<typeof guest.checkout, guest.CheckoutRequest>(guest.checkout, {
    args: (body) => ({ body }),
    idempotencyKey: () => idempotencyKeyFor(`checkout.${scope}`),
    invalidates: [guest.listOrders],
    // The attempt succeeded, so the next checkout is a genuinely new one.
    onSuccess: () => clearIdempotencyKey(`checkout.${scope}`),
  });
}

/**
 * Tells the platform the payment hold succeeded, the moment the gateway says so
 * (GST-005).
 *
 * A SHORTCUT and nothing more. The gateway's webhook confirms every payment on
 * its own, so this exists only to spare the guest the second or two that round
 * trip takes before their order reads as placed. Failure is therefore not worth
 * showing anyone - the caller swallows it and the webhook does the job.
 */
export function useConfirmPayment() {
  return useEndpointMutation<typeof guest.confirmPayment, string>(guest.confirmPayment, {
    args: (orderPublicId) => ({ params: { orderPublicId } }),
    idempotencyKey: (orderPublicId) => idempotencyKeyFor(`confirm.${orderPublicId}`),
    invalidates: [guest.listOrders],
  });
}

/** Self-serve cancellation with an automatic full refund (GST-009). */
export function useCancelOrder(orderPublicId: string) {
  return useEndpointMutation<typeof guest.cancelOrder, string | undefined>(guest.cancelOrder, {
    args: (reason) => ({
      params: { orderPublicId },
      body: reason === undefined ? {} : { reason },
    }),
    idempotencyKey: () => idempotencyKeyFor(`cancel.${orderPublicId}`),
    invalidates: [{ endpoint: guest.getOrder, params: { orderPublicId } }, guest.listOrders],
    onSuccess: () => clearIdempotencyKey(`cancel.${orderPublicId}`),
  });
}

/**
 * "Request an invoice" (invoicing.html#delivery): the baker's product invoice
 * issued on the first press, the same documents on every other - so the
 * request carries no idempotency key of its own. The order is re-read
 * afterwards, which is where the list lives for every later visit.
 */
export function useRequestInvoices(orderPublicId: string) {
  return useEndpointMutation<typeof guest.requestInvoices, void>(guest.requestInvoices, {
    args: () => ({ params: { orderPublicId }, body: {} }),
    invalidates: [{ endpoint: guest.getOrder, params: { orderPublicId } }],
  });
}

/** Post-delivery courier tip, forwarded verbatim (GST-037, PAY-024). */
export function useTipCourier(orderPublicId: string) {
  return useEndpointMutation<typeof guest.tipCourier, number>(guest.tipCourier, {
    args: (amount) => ({ params: { orderPublicId }, body: { amount } }),
    idempotencyKey: () => idempotencyKeyFor(`tip.${orderPublicId}`),
    invalidates: [{ endpoint: guest.getOrder, params: { orderPublicId } }],
    onSuccess: () => clearIdempotencyKey(`tip.${orderPublicId}`),
  });
}

/** Two distinct, skippable ratings (GST-038). */
export interface RatingInput {
  readonly subject: RatingSubject;
  readonly stars: number;
  readonly comment?: string;
}

export function useRateOrder(orderPublicId: string) {
  return useEndpointMutation<typeof guest.rateOrder, readonly RatingInput[]>(guest.rateOrder, {
    args: (ratings) => ({ params: { orderPublicId }, body: { ratings: [...ratings] } }),
    invalidates: [{ endpoint: guest.getOrder, params: { orderPublicId } }],
  });
}

/**
 * Stores the photo and answers with its address (GST-039).
 *
 * Separate from the report on purpose: the picture is prepared and uploaded as
 * it is chosen, so the work happens next to the control that started it, and a
 * guest whose upload fails can still send the report without it.
 */
export function useUploadIssuePhoto(orderPublicId: string) {
  return useEndpointMutation<
    typeof guest.uploadIssuePhoto,
    { data: string; contentType: 'image/jpeg' }
  >(guest.uploadIssuePhoto, { args: (body) => ({ params: { orderPublicId }, body }) });
}

/** Per-line issue report with an optional photo (GST-039). */
export function useReportIssue(orderPublicId: string) {
  return useEndpointMutation<
    typeof guest.reportIssue,
    { lineIds: string[]; text: string; contactEmail: string; photoUrl?: string }
  >(guest.reportIssue, {
    args: (body) => ({ params: { orderPublicId }, body }),
    idempotencyKey: () => idempotencyKeyFor(`issue.${orderPublicId}`),
    onSuccess: () => clearIdempotencyKey(`issue.${orderPublicId}`),
  });
}

/** Rebuilds a past basket for a new morning (GST-010, GST-034). */
export function useReorder() {
  return useEndpointMutation<typeof guest.reorder, string>(guest.reorder, {
    args: (orderPublicId) => ({ params: { orderPublicId } }),
  });
}
